/**
 * Process Engine Execution Types
 * Types for executing processes and workflows
 * Maps to C# backend models in BizFirst.Ai.ProcessEngine.Domain
 */

/**
 * Execution Mode - How the process was triggered
 * Maps to C# eExecutionMode
 */
export enum ExecutionMode {
  Manual = 0,
  Webhook = 1,
  Scheduled = 2,
  Event = 3,
  Test = 4,
  SubProcess = 5,
}

/**
 * Environment Mode - Where the process is executed
 * Maps to C# eEnvironmentMode
 */
export enum EnvironmentMode {
  Production = 0,
  Test = 1,
  Default = 2,
}

/**
 * Designer Mode - Workflow designer mode
 * Maps to C# eDesignerMode
 */
export enum DesignerMode {
  Preview = 0,
  Design = 1,
  Live = 2,
  Review = 3,
  Default = 4,
}

/**
 * Executor Identifier
 * Maps to C# ExecutorID
 */
export interface ExecutorID {
  appID?: number;
  processID?: number;
  processThreadID?: number;
  processElementID?: number;
  processThreadVersionID?: number;
  processExecutionID?: number;
}

/**
 * Execution Identifier
 * Maps to C# ExecutionID (note: backend uses PascalCase)
 */
export interface ExecutionIdentifier {
  ResID?: string;  // PascalCase: GUID string for tracking
  ID?: number;     // PascalCase: numeric ID (from database)
}

/**
 * Process Context Execution Mode
 * Maps to C# ProcessContextExecutionMode
 */
export interface ProcessContextExecutionMode {
  executionMode: ExecutionMode;
  environmentMode: EnvironmentMode;
  designerMode: DesignerMode;
  startedAtUtc: string; // ISO 8601 datetime
}

/**
 * Request Session - Tenant context for multi-tenant isolation
 */
export interface RequestAiSession {
  [key: string]: any;
}

/**
 * Execution Memory - Shared memory across all threads
 */
export interface ExecutionMemory {
  [key: string]: any;
}

/**
 * Process Thread Execution Context
 */
export interface ProcessThreadExecutionContext {
  [key: string]: any;
}

/**
 * Observability Override
 * Optional per-execution override for the process's observability behavior
 * (which event families get logged, payload masking level, slow-threshold).
 *
 * This exact shape is used in THREE places and must stay identical across all of them:
 *   1. As the SettingValue string (JSON.stringify'd) for the user's persisted
 *      "UserObserveSettings" default, via the IAM self-service user-settings API.
 *   2. Here, as ProcessExecutionContext.observabilityOverride, on the Execute request
 *      (sent as a plain object — not stringified — since the whole request is JSON already).
 *   3. Informationally, inside an ExecutionTemplate's own Configuration JSON under an
 *      "observability" key (server-owned; not built by this frontend).
 *
 * Maps to C# ObservabilityOverride (BizFirst.Ai.ProcessEngine Observability domain).
 */
export interface ObservabilityOverride {
  /**
   * Set only when the user actively picked a specific observability profile.
   * Built-in IDs: Production=1, Development=2, QA=3, Eval=4.
   * Omit entirely (do not send 0/undefined-as-present) to mean "no profile override".
   */
  observabilityProfileID?: number;
  /**
   * Set only when "use custom observability settings for this run" is on.
   * A JSON.stringify'd payload of shape:
   *   {
   *     enableCustomSettings: true,
   *     defaultMaskLevel?: 'None' | 'KeyNamesOnly' | 'Full',
   *     slowThresholdMs?: number,
   *     families?: Partial<Record<
   *       'Node' | 'ProgressReport' | 'Hil' | 'Remote' | 'IO' | 'Internal' | 'Performance' | 'Debug',
   *       { enabled: boolean }
   *     >>,
   *   }
   * Field names are exact and match the server-side parser (a workflow node's own
   * "observability" config key uses the identical shape) — do not rename.
   */
  observabilityConfigJson?: string;
}

/**
 * Process Execution Context
 * Maps to C# ProcessExecutionContext
 */
export interface ProcessExecutionContext {
  executorID: ExecutorID;
  executionID: ExecutionIdentifier;
  mode: ProcessContextExecutionMode;
  inputData: Record<string, any>;
  triggerData?: Record<string, any>;
  requestSession?: RequestAiSession;
  memory?: ExecutionMemory;
  threadContexts?: ProcessThreadExecutionContext[];
  currentExecutionState?: number; // eExecutionState enum value
  /** Optional per-run observability override picked via TopToolbar's Advanced Execute modal. */
  observabilityOverride?: ObservabilityOverride;
}

/**
 * Execute Process Request
 * Maps directly to ProcessExecutionContext
 * BaseApiClient.post() will wrap this in { metadata, data } envelope
 */
export interface ExecuteProcessRequest extends ProcessExecutionContext {
  // Inherits all properties from ProcessExecutionContext
  // No additional wrapper needed - post() method handles it
}

/**
 * Execution Data from backend response
 * Maps to C# execution context returned by ProcessEngineService
 */
export interface ExecutionData {
  processExecutionID: number;
  processID: number;
  executionModeID: number;
  executionStatusID: number;
  startedAt: string;
  stoppedAt: string;
  duration: number;
  triggerData?: Record<string, any>;
  inputData: string; // JSON string from backend
  outputData: string; // JSON string from backend
  errorMessage?: string;
  errorStack?: string;
  errorNodeID?: number;
  retryCount: number;
  parentExecutionID?: number;
  totalNodes: number;
  completedNodes: number;
  failedNodes?: number;
  skippedNodes?: number;
  resID: string; // GUID
  [key: string]: any; // Allow additional properties
}

/**
 * Execution State container
 */
export interface ExecutionStateInfo {
  currentExecutionState: number; // eExecutionState enum value
}

/**
 * Get Execution Status Request Data
 * The BaseApiClient.post() wraps this in { metadata, data }
 * So this becomes: { metadata: {...}, data: { ExecutionID: {...} } }
 */
export interface GetExecutionStatusRequestData {
  ExecutionID: ExecutionIdentifier;  // Contains ResID and ID
}

/**
 * Execution Control Request Data
 * The BaseApiClient.post() wraps this in { metadata, data }
 * So this becomes: { metadata: {...}, data: { ExecutionID: {...} } }
 */
export interface ExecutionControlRequestData {
  ExecutionID: ExecutionIdentifier;  // Contains ResID and ID
}

/**
 * Execute Process Response
 * Matches the actual response structure from the backend
 */
export interface ExecuteProcessResponse {
  success: boolean;
  data?: {
    executorID: ExecutorID;
    executionData: ExecutionData;
    executionID: ExecutionIdentifier;
    mode: ProcessContextExecutionMode;
    state: ExecutionStateInfo;
  };
  metadata?: {
    headers: Record<string, any>;
    requestId: string;
    correlationId: string;
    timestamp: string;
    version: string;
    additional: Record<string, any>;
  };
  errors?: Array<{
    code: string;
    message: string;
    field?: string;
    severity: 'Info' | 'Warning' | 'Error';
    details?: Record<string, any>;
  }>;
}
